import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Plus, GitBranch, Merge, CircleDot, Pencil, Maximize2, Minimize2,
  Undo2, Redo2, LayoutGrid, Save, X as XIcon, GitFork, Timer, Layers,
} from 'lucide-react';
import { Button, ICON_PX } from '../ui';
import TaskNode from './nodes/TaskNode';
import GateNode from './nodes/GateNode';
import JoinNode from './nodes/JoinNode';
import EndNode from './nodes/EndNode';
import ParallelNode from './nodes/ParallelNode';
import TimerNode from './nodes/TimerNode';
import SubProcessNode from './nodes/SubProcessNode';
import SwimlaneNode from './nodes/SwimlaneNode';
import EdgeConfigPopover from './EdgeConfigPopover';
import NodeExecutionPopover from './NodeExecutionPopover';
import { elkLayout, quickLayout, NODE_DIMS } from '../../utils/elkLayout';

// Register all custom node types
const nodeTypes = {
  task: TaskNode,
  gate: GateNode,
  join: JoinNode,
  end: EndNode,
  parallel: ParallelNode,
  timer: TimerNode,
  subprocess: SubProcessNode,
  swimlane: SwimlaneNode,
};

/**
 * Topological sort — returns node IDs in dependency order.
 * Loop edges are filtered out before sorting.
 */
function topoSort(nodes, edges) {
  const dagEdges = edges.filter((e) => !e.isLoop);
  const inDegree = {};
  const adj = {};
  nodes.forEach((n) => { inDegree[n.id] = 0; adj[n.id] = []; });
  dagEdges.forEach((e) => {
    const from = e.from || e.source;
    const to = e.to || e.target;
    if (adj[from]) adj[from].push(to);
    inDegree[to] = (inDegree[to] || 0) + 1;
  });
  const queue = nodes.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
  const sorted = [];
  while (queue.length > 0) {
    const id = queue.shift();
    sorted.push(id);
    (adj[id] || []).forEach((to) => {
      inDegree[to]--;
      if (inDegree[to] === 0) queue.push(to);
    });
  }
  return sorted;
}

const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: 'var(--slate)' },
  style: { stroke: 'var(--slate)', strokeWidth: 2 },
  animated: false,
};

/**
 * DAG workflow canvas — supports build and execute modes.
 * Uses elkjs for layout (compound nodes, swimlanes, hierarchical).
 *
 * Build mode: nodes are draggable, edges are connectable, toolbar visible.
 * Execute mode: nodes show status, clicking opens completion in sidebar.
 */
function WorkflowCanvas({
  workflow,
  nodeStates = {},
  mode = 'build',
  swimlanes = [],
  onNodeClick,
  onConnect: externalConnect,
  onAddNode,
  onModeToggle,
  onToggleFullscreen,
  isFullscreen = false,
  onSave,
  onCancel,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  isDirty = false,
  onAutoLayout,
  onUpdateEdge,
  onDeleteEdge,
  onDeleteNode,
  // Execute mode: completion handlers for node popover
  onCompleteTask,
  onSkipTask,
  onGateSelect,
}) {
  const isBuild = mode === 'build';
  const [edgePopover, setEdgePopover] = useState(null);
  const [execPopover, setExecPopover] = useState(null); // { node, nodeState, x, y }
  const layoutPendingRef = useRef(false);

  // Assign step numbers to task nodes in topological order
  const stepNumbers = useMemo(() => {
    if (!workflow?.nodes || !workflow?.edges) return {};
    const sorted = topoSort(workflow.nodes, workflow.edges);
    const nums = {};
    let step = 1;
    sorted.forEach((id) => {
      const n = workflow.nodes.find((nd) => nd.id === id);
      if (n?.type === 'task') { nums[id] = step++; }
    });
    return nums;
  }, [workflow]);

  const initialNodes = useMemo(() => {
    const raw = (workflow?.nodes || []).map((n) => {
      const dims = NODE_DIMS[n.type] || NODE_DIMS.task;
      return {
        id: n.id,
        type: n.type,
        position: n.position || { x: 0, y: 0 },
        // ReactFlow Group node setup for compound types
        ...(n.type === 'swimlane' ? { type: 'swimlane', style: { width: dims.width, height: dims.height } } : {}),
        ...(n.parentId ? { parentNode: n.parentId, extent: 'parent' } : {}),
        data: {
          ...n,
          label: n.label,
          domain: n.domain,
          completionType: n.completionType,
          estimatedMinutes: n.estimatedMinutes,
          options: n.options,
          duration: n.duration,
          linkedWorkflowId: n.linkedWorkflowId,
          role: n.role,
          status: nodeStates[n.id]?.status || 'pending',
          satisfiedBy: nodeStates[n.id]?.satisfiedBy,
          satisfiedAt: nodeStates[n.id]?.satisfiedAt,
          completionSource: nodeStates[n.id]?.completionSource,
          stepNumber: stepNumbers[n.id],
          slaWarning: nodeStates[n.id]?.slaWarning,
          slaBreach: nodeStates[n.id]?.slaBreach,
        },
      };
    });

    // Use saved positions if all nodes have non-zero positions; otherwise quick layout
    const allPositioned = raw.length > 0 && raw.every((n) => n.position.x !== 0 || n.position.y !== 0);
    if (allPositioned) return raw;
    return quickLayout(raw, workflow?.edges || []);
  }, [workflow, nodeStates, stepNumbers]);

  const initialEdges = useMemo(() =>
    (workflow?.edges || []).map((e) => {
      const targetSatisfied = nodeStates[e.to]?.status === 'satisfied';
      const sourceSatisfied = nodeStates[e.from]?.status === 'satisfied';
      const bothDone = targetSatisfied && sourceSatisfied;
      const isLoop = !!e.isLoop;
      const hasCondition = !!e.guard;
      return {
        id: e.id,
        source: e.from,
        target: e.to,
        sourceHandle: e.fromPort,
        label: e.label || (hasCondition ? `if ${e.guard}` : undefined),
        data: { isLoop, guard: e.guard },
        ...defaultEdgeOptions,
        type: 'smoothstep',
        animated: targetSatisfied || isLoop,
        style: {
          ...defaultEdgeOptions.style,
          stroke: isLoop ? 'var(--aqua)' : (bothDone ? 'var(--green)' : targetSatisfied ? 'var(--aqua)' : hasCondition ? 'var(--yellow)' : 'var(--slate)'),
          strokeDasharray: isLoop ? '5 5' : hasCondition ? '8 4' : (!sourceSatisfied && !targetSatisfied ? '6 4' : undefined),
          opacity: isLoop ? 0.7 : (!sourceSatisfied && !targetSatisfied ? 0.4 : 1),
        },
        markerEnd: {
          ...defaultEdgeOptions.markerEnd,
          color: isLoop ? 'var(--aqua)' : (bothDone ? 'var(--green)' : targetSatisfied ? 'var(--aqua)' : 'var(--slate)'),
        },
      };
    }),
    [workflow, nodeStates]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Reset internal state when workflow id changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [workflow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync node data when nodeStates change or workflow edits come in
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Async elkjs layout — run once when nodes are all at (0,0) or on tidy request
  useEffect(() => {
    if (!workflow?.nodes?.length || !workflow?.edges?.length) return;
    const allAtOrigin = workflow.nodes.every((n) => !n.position || (n.position.x === 0 && n.position.y === 0));
    if (allAtOrigin && !layoutPendingRef.current) {
      layoutPendingRef.current = true;
      elkLayout(workflow.nodes, workflow.edges, { swimlanes })
        .then(({ nodes: positioned }) => {
          setNodes((current) =>
            current.map((n) => {
              const p = positioned.find((pn) => pn.id === n.id);
              return p ? { ...n, position: p.position } : n;
            })
          );
          layoutPendingRef.current = false;
        })
        .catch(() => { layoutPendingRef.current = false; });
    }
  }, [workflow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = useCallback(
    (params) => {
      if (params.source === params.target) return;
      setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds));
      externalConnect?.(params);
    },
    [setEdges, externalConnect]
  );

  const handleNodeClick = useCallback(
    (event, node) => {
      setEdgePopover(null);
      if (!isBuild) {
        // Execute mode: show inline popover near the node
        setExecPopover({
          node,
          nodeState: nodeStates[node.id],
          x: event.clientX,
          y: event.clientY,
        });
      }
      onNodeClick?.(node);
    },
    [isBuild, nodeStates, onNodeClick]
  );

  const handleEdgeClick = useCallback(
    (event, edge) => {
      if (!isBuild) return;
      event.stopPropagation();
      setEdgePopover({
        edge: { id: edge.id, label: edge.label, isLoop: edge.data?.isLoop, guard: edge.data?.guard },
        x: event.clientX,
        y: event.clientY,
      });
    },
    [isBuild]
  );

  const handlePaneClick = useCallback(() => {
    setEdgePopover(null);
    setExecPopover(null);
    onNodeClick?.(null);
  }, [onNodeClick]);

  const handleNodesDelete = useCallback(
    (deleted) => {
      if (!isBuild) return;
      deleted.forEach((n) => onDeleteNode?.(n.id));
    },
    [isBuild, onDeleteNode]
  );

  const handleEdgesDelete = useCallback(
    (deleted) => {
      if (!isBuild) return;
      deleted.forEach((e) => onDeleteEdge?.(e.id));
    },
    [isBuild, onDeleteEdge]
  );

  // Tidy layout handler — triggers elkjs async re-layout
  const handleTidy = useCallback(() => {
    if (!workflow?.nodes?.length) return;
    elkLayout(workflow.nodes, workflow.edges || [], { swimlanes })
      .then(({ nodes: positioned }) => {
        setNodes((current) =>
          current.map((n) => {
            const p = positioned.find((pn) => pn.id === n.id);
            return p ? { ...n, position: p.position } : n;
          })
        );
        onAutoLayout?.();
      })
      .catch(() => {});
  }, [workflow, swimlanes, setNodes, onAutoLayout]);

  // Compute progress for execute mode
  const progress = useMemo(() => {
    if (!workflow?.nodes) return { done: 0, total: 0 };
    const taskNodes = workflow.nodes.filter((n) => n.type === 'task');
    const satisfied = taskNodes.filter((n) => nodeStates[n.id]?.status === 'satisfied').length;
    return { done: satisfied, total: taskNodes.length };
  }, [workflow, nodeStates]);

  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const progressColor = progress.done === progress.total ? 'bg-green-nn' : progressPct >= 50 ? 'bg-[var(--yellow)]' : 'bg-[var(--red-critical)]';

  return (
    <div className="flex flex-col h-full rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          {isBuild ? (
            <>
              {/* Add-node toolbar */}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('task')} title="Add a task step">
                  <Plus size={ICON_PX.sm} className="mr-1" /> Task
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('gate')} title="Add a decision">
                  <GitBranch size={ICON_PX.sm} className="mr-1" /> Decision
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('join')} title="Add a wait point">
                  <Merge size={ICON_PX.sm} className="mr-1" /> Wait
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('end')} title="Add a finish">
                  <CircleDot size={ICON_PX.sm} className="mr-1" /> Finish
                </Button>
              </div>

              {/* Separator */}
              <div className="w-px h-5 bg-slate-200" />

              {/* New node types */}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('parallel')} title="Add parallel tracks">
                  <GitFork size={ICON_PX.sm} className="mr-1" /> Parallel
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('timer')} title="Add a timer/SLA">
                  <Timer size={ICON_PX.sm} className="mr-1" /> Timer
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onAddNode?.('subprocess')} title="Add a sub-process">
                  <Layers size={ICON_PX.sm} className="mr-1" /> Sub-proc
                </Button>
              </div>

              {/* Separator */}
              <div className="w-px h-5 bg-slate-200" />

              {/* Utility toolbar */}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)">
                  <Undo2 size={ICON_PX.sm} />
                </Button>
                <Button variant="ghost" size="sm" onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
                  <Redo2 size={ICON_PX.sm} />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleTidy} title="Tidy layout (elkjs)">
                  <LayoutGrid size={ICON_PX.sm} className="mr-1" /> Tidy
                </Button>
              </div>
            </>
          ) : (
            /* Execute progress */
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Progress</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-text-dark">{progress.done}/{progress.total}</span>
                <div className="w-28 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${progressColor}`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {!isBuild && (
            <Button variant="outline" size="sm" onClick={onModeToggle}>
              <Pencil size={ICON_PX.sm} className="mr-1" /> Edit
            </Button>
          )}
          {onToggleFullscreen && (
            <Button variant="ghost" size="sm" onClick={onToggleFullscreen} title={isFullscreen ? 'Minimize' : 'Expand'}>
              {isFullscreen ? <Minimize2 size={ICON_PX.md} /> : <Maximize2 size={ICON_PX.md} />}
            </Button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={isBuild ? onNodesChange : undefined}
          onEdgesChange={isBuild ? onEdgesChange : undefined}
          onConnect={isBuild ? handleConnect : undefined}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          onPaneClick={handlePaneClick}
          onNodesDelete={handleNodesDelete}
          onEdgesDelete={handleEdgesDelete}
          nodeTypes={nodeTypes}
          nodesDraggable={isBuild}
          nodesConnectable={isBuild}
          edgesUpdatable={isBuild}
          elementsSelectable={true}
          deleteKeyCode={isBuild ? ['Backspace', 'Delete'] : null}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          minZoom={0.3}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#e2e8f0" gap={24} size={1} variant="dots" />
          <Controls
            showInteractive={false}
            className="!border-slate-200 !rounded-xl !shadow-sm !bg-white/90 !backdrop-blur-sm"
          />
        </ReactFlow>

        {edgePopover && (
          <EdgeConfigPopover
            edge={edgePopover.edge}
            position={{ x: edgePopover.x, y: edgePopover.y }}
            onUpdateEdge={onUpdateEdge}
            onDeleteEdge={onDeleteEdge}
            onClose={() => setEdgePopover(null)}
          />
        )}

        {execPopover && !isBuild && (
          <NodeExecutionPopover
            node={execPopover.node}
            nodeState={execPopover.nodeState}
            position={{ x: execPopover.x, y: execPopover.y }}
            onCompleteTask={onCompleteTask}
            onSkipTask={onSkipTask}
            onGateSelect={onGateSelect}
            onClose={() => setExecPopover(null)}
          />
        )}
      </div>

      {/* Save / Cancel footer (build mode only) */}
      {isBuild && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {isDirty ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[var(--yellow)]" />
                <span>Unsaved changes</span>
              </>
            ) : (
              <span>All changes saved</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <XIcon size={ICON_PX.sm} className="mr-1" /> Cancel
            </Button>
            <Button variant="save" size="sm" onClick={onSave} disabled={!isDirty}>
              <Save size={ICON_PX.sm} className="mr-1" /> Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkflowCanvas;
